<template>
  <div class="questionnaire-page">
    <!-- 标题栏已由父组件显示，这里隐藏避免重复 -->
    
    <el-card class="box-card" v-if="questionnaireTemplate">
      <div class="card">
        <div class="questionnaire-header">
          <h2>职业健康调查问卷</h2>
          
          <!-- ✅ 显眼提示：问卷做完后系统才会提交考卷 -->
          <div class="questionnaire-warning">
            <span class="warning-icon">⚠</span>
            <span class="warning-text">重要提示：问卷做完后系统才会提交考卷，请务必完成所有问题。</span>
          </div>
          
          <div class="questionnaire-intro">
            <p>您好！</p>
            <p>我们邀请您参加一项职业健康状况问卷调查，本问卷调查旨在了解您的职业心理、身体活动水平、肌肉骨骼疾患的基本情况，为职业人群的健康保护提供依据。我们保证对您提供的个人信息资料进行保密，并承诺所有资料仅用于学术研究。谢谢您的合作！</p>
            <p>广东省职业健康协会</p>
          </div>
        </div>
        
        <!-- 按 displayIndex 顺序渲染可见题目 -->
        <template v-for="question in visibleQuestions" :key="question.id">
          <!-- Likert复合题 -->
          <LikertCompositeQuestion
            v-if="question.type === 'LIKERT_COMPOSITE'"
            :ref="el => setQuestionRef(question.id, el)"
            :question-id="question.id"
            :display-index="question.displayIndex"
            :title="question.title"
            :required="question.required"
            :has-error="questionErrors.has(question.id)"
            :likert-config="question.compositeConfig.likert.scaleConfig"
            :sub-questions="question.compositeConfig.likert.subQuestions"
            :model-value="answers"
            @update:modelValue="handleAnswersUpdate"
          />
          
          <!-- 其他题型（基础题型、矩阵题、疼痛评分题等） -->
          <QuestionnaireItem
            v-else
            :ref="el => setQuestionRef(question.id, el)"
            :question="question"
            :current-index="question.displayIndex"
            :total-count="viewQuestions.length"
            :show-progress="true"
            :all-answers="answers"
            :has-error="questionErrors.has(question.id)"
            v-model="answers[question.id]"
          />
        </template>
        
        <!-- 提交按钮（在内容流中的原始位置） -->
        <div
          v-if="isScrolledToBottom"
          class="gradient-background submit-btn relative-position"
          :class="{ 
            'submitting': submitting, 
            'has-errors': !canSubmit && !submitting
          }"
          @click="handleSubmitClick"
          v-loading="submitting"
          :title="canSubmit ? '可以提交' : '请完成所有必填项后提交'"
        >
          提交问卷
        </div>
      </div>
    </el-card>
    
    <!-- 提交按钮（固定定位，悬浮在底部） -->
    <div
      v-if="questionnaireTemplate && !isScrolledToBottom"
      class="gradient-background submit-btn fixed-bottom"
      :class="{ 
        'submitting': submitting, 
        'has-errors': !canSubmit && !submitting
      }"
      @click="handleSubmitClick"
      v-loading="submitting"
      :title="canSubmit ? '可以提交' : '请完成所有必填项后提交'"
    >
      提交问卷
    </div>
    
    <div v-else-if="!questionnaireTemplate" class="loading-container">
      <div class="loading-text">加载问卷模板中...</div>
    </div>
  </div>
</template>

<script>
import QuestionnaireItem from "@/components/QuestionnaireItem.vue";
import LikertCompositeQuestion from "@/components/LikertCompositeQuestion.vue";
import { get, post } from "@/request.js";
import { transformBackendToView, calculateVisibleQuestions } from "@/utils/questionnaireTransformer.js";
import { getStorage, setStorage, removeStorage } from "@/assets/js/storage.js";

export default {
  name: "Questionnaire",
  components: {
    QuestionnaireItem,
    LikertCompositeQuestion
  },
  props: {
    examId: {
      type: Number,
      required: true
    },
    templateCode: {
      type: String,
      default: null
    }
  },
  emits: ['submit-success', 'go-back'],
  data() {
    return {
      questionnaireTemplate: null,
      viewQuestions: [],  // 转换后的前端渲染模型
      answers: {},
      submitting: false,
      saveCacheTimer: null,  // 防抖定时器
      questionRefs: new Map(),  // 题目组件的 ref 引用
      questionErrors: new Set(),  // 有错误的题目ID集合
      isScrolledToBottom: false  // 是否滚动到底部
    };
  },
  computed: {
    // 按 displayIndex 排序的题目列表（确保显示顺序正确）
    sortedViewQuestions() {
      if (!this.viewQuestions || this.viewQuestions.length === 0) {
        return [];
      }
      return [...this.viewQuestions].sort((a, b) => {
        return (a.displayIndex || 0) - (b.displayIndex || 0);
      });
    },
    // 根据答案和跳转逻辑计算可见题目列表
    visibleQuestions() {
      if (!this.sortedViewQuestions || this.sortedViewQuestions.length === 0) {
        return [];
      }
      return calculateVisibleQuestions(this.sortedViewQuestions, this.answers);
    },
    // 获取用户ID（用于缓存key）
    studentId() {
      try {
        const userInfo = JSON.parse(sessionStorage.getItem("userInfo") || "{}");
        return userInfo.id || null;
      } catch (e) {
        return null;
      }
    },
    // 生成缓存key：questionnaire_draft_{examId}_{studentId}_{templateCode}
    cacheKey() {
      if (!this.examId || !this.studentId) {
        return null;
      }
      // 优先使用 props 中的 templateCode，如果没有则从 questionnaireTemplate 获取
      const templateCode = this.templateCode || this.questionnaireTemplate?.templateCode;
      if (!templateCode) {
        return null;
      }
      return `questionnaire_draft_${this.examId}_${this.studentId}_${templateCode}`;
    },
    canSubmit() {
      // 检查必填项是否都已填写（只检查可见题目）
      if (!this.visibleQuestions || this.visibleQuestions.length === 0) {
        console.log('[问卷调试] canSubmit: visibleQuestions 为空');
        return false;
      }
      
      const result = this.visibleQuestions.every(question => {
        if (!question.required) {
          return true;
        }
        
        // Likert复合题特殊处理
        if (question.type === 'LIKERT_COMPOSITE') {
          const compositeAnswers = this.answers[question.id];
          if (!compositeAnswers || typeof compositeAnswers !== 'object') {
            console.log('[问卷调试] canSubmit: Likert复合题未填写', {
              questionId: question.id,
              compositeAnswers: compositeAnswers
            });
            return false;
          }
          // 检查所有子问题是否都已填写
          const allSubQuestionsAnswered = question.compositeConfig.likert.subQuestions.every(subQ => {
            const subAnswer = compositeAnswers[subQ.id];
            return subAnswer !== null && subAnswer !== undefined;
          });
          if (!allSubQuestionsAnswered) {
            console.log('[问卷调试] canSubmit: Likert复合题子问题未全部填写', {
              questionId: question.id,
              compositeAnswers: compositeAnswers
            });
          }
          return allSubQuestionsAnswered;
        }
        
        // 疼痛评分题特殊处理（现在分为两个独立题目）
        // 是否题必须填写
        if (question.painRatingRelated?.isYesNoQuestion) {
          const answer = this.answers[question.id];
          const isValid = answer !== null && answer !== undefined;
          if (!isValid) {
            console.log('[问卷调试] canSubmit: 疼痛是否题未填写', {
              questionId: question.id,
              answer: answer
            });
          }
          return isValid;
        }
        // 评分题：如果关联的是否题选"是"，则必须填写；如果选"否"，可以不填
        if (question.type === 'RATING' && question.painRatingRelated?.isRatingQuestion) {
          const yesNoQuestionId = question.painRatingRelated.yesNoQuestionId;
          const yesNoAnswer = this.answers[yesNoQuestionId];
          // 如果是否题未选择，评分题不能填写
          if (!yesNoAnswer || yesNoAnswer === 'no') {
            return true; // 选"否"时，评分题可以不填
          }
          // 如果选择"是"，评分题必须填写
          const ratingAnswer = this.answers[question.id];
          const isValid = ratingAnswer !== null && ratingAnswer !== undefined;
          if (!isValid) {
            console.log('[问卷调试] canSubmit: 疼痛评分题未填写（是否题选"是"）', {
              questionId: question.id,
              yesNoAnswer: yesNoAnswer,
              ratingAnswer: ratingAnswer
            });
          }
          return isValid;
        }
        
        // 矩阵题特殊处理
        if (question.type === 'MATRIX') {
          const matrixAnswer = this.answers[question.id];
          if (!matrixAnswer || typeof matrixAnswer !== 'object' || Object.keys(matrixAnswer).length === 0) {
            console.log('[问卷调试] canSubmit: 矩阵题未填写', {
              questionId: question.id,
              questionType: question.type,
              answer: matrixAnswer
            });
            return false;
          }
          // 检查矩阵题的所有行是否都已填写
          const allRowsAnswered = question.matrixConfig?.rows?.every(row => {
            return matrixAnswer[row.id] !== null && matrixAnswer[row.id] !== undefined;
          });
          if (!allRowsAnswered) {
            console.log('[问卷调试] canSubmit: 矩阵题未全部填写', {
              questionId: question.id,
              questionType: question.type,
              answer: matrixAnswer
            });
            return false;
          }
          return true;
        }
        
        // 其他题型
        const answer = this.answers[question.id];
        if (answer === null || answer === undefined) {
          console.log('[问卷调试] canSubmit: 必填题未填写', {
            questionId: question.id,
            questionType: question.type,
            answer: answer
          });
          return false;
        }
        
        if (typeof answer === 'string' && answer.trim() === '') {
          console.log('[问卷调试] canSubmit: 必填题答案为空字符串', {
            questionId: question.id,
            questionType: question.type,
            answer: answer
          });
          return false;
        }
        
        if (Array.isArray(answer) && answer.length === 0) {
          console.log('[问卷调试] canSubmit: 必填多选题未选择', {
            questionId: question.id,
            questionType: question.type,
            answer: answer
          });
          return false;
        }
        
        return true;
      });
      
      // 详细记录每个必填题目的状态
      const requiredQuestionsStatus = this.viewQuestions
        .filter(q => q.required)
        .map(q => ({
          id: q.id,
          type: q.type,
          title: q.title?.substring(0, 20) || '',
          hasAnswer: this.answers.hasOwnProperty(q.id),
          answer: this.answers[q.id],
          isValid: (() => {
            if (q.type === 'LIKERT_COMPOSITE') {
              const compositeAnswers = this.answers[q.id];
              if (!compositeAnswers || typeof compositeAnswers !== 'object') {
                return false;
              }
              return q.compositeConfig.likert.subQuestions.every(subQ => {
                const subAnswer = compositeAnswers[subQ.id];
                return subAnswer !== null && subAnswer !== undefined;
              });
            }
            const answer = this.answers[q.id];
            if (answer === null || answer === undefined) return false;
            if (typeof answer === 'string' && answer.trim() === '') return false;
            if (Array.isArray(answer) && answer.length === 0) return false;
            return true;
          })()
        }));
      
      console.log('[问卷调试] canSubmit 结果:', {
        result: result,
        viewQuestionsCount: this.viewQuestions.length,
        requiredQuestionsCount: this.viewQuestions.filter(q => q.required).length,
        answersKeys: Object.keys(this.answers),
        requiredQuestionsStatus: requiredQuestionsStatus
      });
      
      return result;
    }
  },
  mounted() {
    this.loadQuestionnaireTemplate();
    // 添加滚动监听（监听 window 和内容区域的滚动）
    this.handleScroll = this.checkScrollPosition.bind(this);
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    // 初始检查一次（延迟检查，确保DOM已渲染）
    // 先设置为 false，确保按钮默认显示为固定定位
    this.isScrolledToBottom = false;
    this.$nextTick(() => {
      // 延迟检查，确保页面完全加载和渲染
      setTimeout(() => {
        this.checkScrollPosition();
      }, 500);
    });
  },
  beforeUnmount() {
    // 组件销毁时清除防抖定时器
    if (this.saveCacheTimer) {
      clearTimeout(this.saveCacheTimer);
      this.saveCacheTimer = null;
    }
    // 移除滚动监听
    if (this.handleScroll) {
      window.removeEventListener('scroll', this.handleScroll);
    }
  },
  watch: {
    // 监听 answers 变化，自动保存到 localStorage（防抖处理）
    answers: {
      handler(newAnswers, oldAnswers) {
        this.saveAnswersToCache(newAnswers);
        // 当用户填写答案后，自动清除该题目的错误状态
        this.clearErrorsForAnsweredQuestions(newAnswers);
        
        // 检查跳转逻辑，如果触发跳转，滚动到目标题目
        this.$nextTick(() => {
          this.handleSkipLogic(newAnswers, oldAnswers);
        });
      },
      deep: true
    }
  },
  methods: {
    async loadQuestionnaireTemplate() {
      try {
        const res = await get(`/api/my/exam/${this.examId}/questionnaire`);
        if (res.success && res.data) {
          this.questionnaireTemplate = res.data;
          
          // 数据转换：后端模型 → 前端渲染模型
          this.viewQuestions = transformBackendToView(this.questionnaireTemplate.questions);
          
          // 初始化答案对象
          // Vue 3 中不需要 $set，直接赋值即可（响应式系统会自动处理）
          // 但是需要确保所有题目的答案都被初始化，否则 v-model 绑定可能无法正确工作
          this.questionnaireTemplate.questions.forEach(question => {
            if (question.type === 'MULTIPLE_CHOICE') {
              this.answers[question.id] = [];
            } else if (question.type === 'LIKERT_SCALE' && question.parentQuestionId) {
              // Likert子题的答案存储在复合题的 answers 对象中
              // 初始化时不需要单独处理，由 LikertCompositeQuestion 组件管理
              // 但为了确保响应式，仍然初始化
              if (this.answers[question.id] === undefined) {
                this.answers[question.id] = null;
              }
            } else if (question.type === 'MATRIX') {
              // 矩阵题：答案格式为 {rowId: columnValue}，初始化为空对象
              this.answers[question.id] = {};
            } else if (question.type === 'SINGLE_CHOICE' && question.options && 
                       question.options.some(opt => opt.value === 'no' || opt.label === '否') &&
                       question.options.some(opt => opt.value === 'yes' || opt.label === '是')) {
              // 疼痛是否题：暂时不选，默认为null
              this.answers[question.id] = null;
            } else {
              this.answers[question.id] = null;
            }
          });
          
          // 初始化 Likert 复合题的答案对象（空对象）
          this.viewQuestions
            .filter(q => q.type === 'LIKERT_COMPOSITE')
            .forEach(composite => {
              if (!this.answers[composite.id]) {
                this.answers[composite.id] = {};
              }
              // 为每个子问题初始化 null
              composite.compositeConfig.likert.subQuestions.forEach(subQ => {
                if (this.answers[composite.id][subQ.id] === undefined) {
                  this.answers[composite.id][subQ.id] = null;
                }
              });
            });
          
          // 调试：记录初始化后的答案对象
          console.log('[问卷调试] 答案初始化完成:', {
            answersKeys: Object.keys(this.answers),
            viewQuestionsCount: this.viewQuestions.length,
            requiredQuestionsCount: this.viewQuestions.filter(q => q.required).length
          });
          
          // 尝试从 localStorage 恢复已保存的答案（断点续填）
          // 注意：必须在答案初始化完成后才能恢复缓存
          this.$nextTick(() => {
            this.restoreAnswersFromCache();
          });
          
          // 初始化疼痛评分题的答案（现在分为两个独立题目，不需要特殊处理）
          // 是否题和评分题会通过上面的基础题型初始化逻辑处理
        } else {
          this.$message.error(res.message || '获取问卷模板失败');
          this.goBack();
        }
      } catch (error) {
        console.error('加载问卷模板失败:', error);
        this.$message.error('加载问卷模板失败，请稍后重试');
        this.goBack();
      }
    },
    
    handleSubmitClick() {
      if (!this.canSubmit) {
        // 清除之前的错误标记
        this.questionErrors.clear();
        
        // 获取可见题目ID集合（排除被跳转逻辑跳过的题目）
        const visibleQuestionIds = new Set(this.visibleQuestions.map(q => q.id));
        
        // 找到所有未填写的必填题（只检查可见的题目）
        const uncompletedQuestions = this.viewQuestions
          .filter(q => q.required && visibleQuestionIds.has(q.id))  // 只验证可见的必填题
          .filter(q => {
            // Likert复合题
            if (q.type === 'LIKERT_COMPOSITE') {
              const compositeAnswers = this.answers[q.id];
              if (!compositeAnswers || typeof compositeAnswers !== 'object') {
                return true;
              }
              return !q.compositeConfig.likert.subQuestions.every(subQ => {
                const subAnswer = compositeAnswers[subQ.id];
                return subAnswer !== null && subAnswer !== undefined;
              });
            }
            
            // 疼痛评分题特殊处理
            if (q.painRatingRelated?.isYesNoQuestion) {
              const answer = this.answers[q.id];
              return answer === null || answer === undefined;
            }
            
            if (q.type === 'RATING' && q.painRatingRelated?.isRatingQuestion) {
              const yesNoQuestionId = q.painRatingRelated.yesNoQuestionId;
              const yesNoAnswer = this.answers[yesNoQuestionId];
              // 如果是否题选"是"，评分题必须填写
              if (yesNoAnswer === 'yes') {
                const ratingAnswer = this.answers[q.id];
                return ratingAnswer === null || ratingAnswer === undefined;
              }
              // 如果选"否"，可以不填
              return false;
            }
            
            // 矩阵题
            if (q.type === 'MATRIX') {
              const matrixAnswer = this.answers[q.id];
              if (!matrixAnswer || typeof matrixAnswer !== 'object' || Object.keys(matrixAnswer).length === 0) {
                return true;
              }
              return !q.matrixConfig?.rows?.every(row => {
                return matrixAnswer[row.id] !== null && matrixAnswer[row.id] !== undefined;
              });
            }
            
            // 单选题：检查"其他"选项的自定义输入
            if (q.type === 'SINGLE_CHOICE') {
              const answer = this.answers[q.id];
              if (answer === null || answer === undefined) return true;
              if (typeof answer === 'string' && answer.trim() === '') return true;
              
              // 检查是否有"其他"选项（allowCustomInput=true）
              const otherOption = q.options?.find(opt => opt.allowCustomInput === true);
              if (otherOption) {
                // 如果选择了"其他"选项
                const answerStr = String(answer);
                const selectedValue = answerStr.includes('|') ? answerStr.split('|')[0] : answerStr;
                if (selectedValue === otherOption.value) {
                  // 必须填写自定义内容（答案格式必须是"选项值|自定义文本"）
                  if (!answerStr.includes('|')) {
                    return true; // 未填写自定义内容
                  }
                  const customText = answerStr.split('|')[1];
                  if (!customText || customText.trim() === '') {
                    return true; // 自定义内容为空
                  }
                }
              }
              return false;
            }
            
            // 其他题型
            const answer = this.answers[q.id];
            if (answer === null || answer === undefined) return true;
            if (typeof answer === 'string' && answer.trim() === '') return true;
            if (Array.isArray(answer) && answer.length === 0) return true;
            return false;
          });
        
        if (uncompletedQuestions.length > 0) {
          // 标记所有未填写的题目为错误状态
          uncompletedQuestions.forEach(q => {
            this.questionErrors.add(q.id);
          });
          
          // 找到第一个未填写的题目并滚动到它
          const firstUncompleted = uncompletedQuestions[0];
          this.scrollToQuestion(firstUncompleted.id);
        }
        return;
      }
      
      // 清除错误标记
      this.questionErrors.clear();
      this.submitQuestionnaire();
    },
    
    // 设置题目组件的 ref
    setQuestionRef(questionId, el) {
      if (el) {
        this.questionRefs.set(questionId, el);
      } else {
        this.questionRefs.delete(questionId);
      }
    },
    
    // 处理跳转逻辑
    handleSkipLogic(newAnswers, oldAnswers) {
      if (!this.sortedViewQuestions || this.sortedViewQuestions.length === 0) {
        return;
      }
      
      // 找出答案发生变化的题目
      const changedQuestions = this.sortedViewQuestions.filter(q => {
        const newAnswer = newAnswers[q.id];
        const oldAnswer = oldAnswers?.[q.id];
        return newAnswer !== oldAnswer && newAnswer !== null && newAnswer !== undefined;
      });
      
      // 检查每个变化题目的跳转逻辑
      for (const question of changedQuestions) {
        const skipLogic = question.skipLogic;
        if (!skipLogic) continue;
        
        const answer = newAnswers[question.id];
        const answerValue = String(answer);
        let shouldSkip = false;
        
        if (skipLogic.condition === 'EQUALS') {
          shouldSkip = answerValue === skipLogic.conditionValue;
        } else if (skipLogic.condition === 'NOT_EQUALS') {
          shouldSkip = answerValue !== skipLogic.conditionValue;
        }
        
        if (shouldSkip) {
          // 如果跳过的题目有答案，清空它
          if (skipLogic.targetQuestionId) {
            // 跳转到指定题目，清空跳过的题目答案
            const skippedQuestionIds = this.getSkippedQuestionIds(question.id, skipLogic.targetQuestionId);
            skippedQuestionIds.forEach(skippedId => {
              // 清空被跳过题目的答案
              const skippedQuestion = this.sortedViewQuestions.find(q => q.id === skippedId);
              if (skippedQuestion) {
                if (skippedQuestion.type === 'MULTIPLE_CHOICE') {
                  this.answers[skippedId] = [];
                } else if (skippedQuestion.type === 'MATRIX') {
                  this.answers[skippedId] = {};
                } else {
                  this.answers[skippedId] = null;
                }
              }
            });
            
            // 滚动到目标题目
            setTimeout(() => {
              this.scrollToQuestion(skipLogic.targetQuestionId);
            }, 300); // 延迟300ms，等待DOM更新
          } else {
            // 跳过下一题，清空下一题的答案
            const currentIndex = this.sortedViewQuestions.findIndex(q => q.id === question.id);
            if (currentIndex >= 0 && currentIndex + 1 < this.sortedViewQuestions.length) {
              const nextQuestion = this.sortedViewQuestions[currentIndex + 1];
              if (nextQuestion.type === 'MULTIPLE_CHOICE') {
                this.answers[nextQuestion.id] = [];
              } else if (nextQuestion.type === 'MATRIX') {
                this.answers[nextQuestion.id] = {};
              } else {
                this.answers[nextQuestion.id] = null;
              }
            }
          }
        }
      }
    },
    
    // 获取被跳过的题目ID列表（从当前题目到目标题目之间的所有题目）
    getSkippedQuestionIds(currentQuestionId, targetQuestionId) {
      const currentIndex = this.sortedViewQuestions.findIndex(q => q.id === currentQuestionId);
      const targetIndex = this.sortedViewQuestions.findIndex(q => q.id === targetQuestionId);
      
      if (currentIndex < 0 || targetIndex < 0 || targetIndex <= currentIndex) {
        return [];
      }
      
      const skippedIds = [];
      for (let i = currentIndex + 1; i < targetIndex; i++) {
        skippedIds.push(this.sortedViewQuestions[i].id);
      }
      return skippedIds;
    },
    
    // 滚动到指定题目
    scrollToQuestion(questionId) {
      this.$nextTick(() => {
        // 方案1：尝试使用 ref
        const questionRef = this.questionRefs.get(questionId);
        if (questionRef && questionRef.$el) {
          const element = questionRef.$el;
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
          
          // 添加一个短暂的闪烁效果，突出显示错误题目
          element.classList.add('error-highlight');
          setTimeout(() => {
            element.classList.remove('error-highlight');
          }, 2000);
          return;
        }
        
        // 方案2：尝试通过题目序号查找（更可靠）
        const question = this.viewQuestions.find(q => q.id === questionId);
        if (question) {
          // 提取题目序号（从ID中提取，如 q24 -> 24）
          const questionNumberMatch = questionId.match(/q(\d+)/);
          const questionNumber = questionNumberMatch ? questionNumberMatch[1] : null;
          
          if (questionNumber) {
            // 通过题目序号查找（更精确）
            const numberElements = Array.from(document.querySelectorAll('.question-number'));
            const targetNumberElement = numberElements.find(el => {
              const text = el.textContent?.trim();
              return text === `${questionNumber}.` || text === `${questionNumber}。`;
            });
            
            if (targetNumberElement) {
              const parentElement = targetNumberElement.closest('.questionnaire-item, .question-item');
              if (parentElement) {
                parentElement.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center'
                });
                parentElement.classList.add('error-highlight');
                setTimeout(() => {
                  parentElement.classList.remove('error-highlight');
                }, 2000);
                return;
              }
            }
          }
          
          // 方案3：通过题目标题查找（降级方案）
          console.warn(`[问卷调试] 无法找到题目 ${questionId} 的 ref，尝试使用 DOM 查询`);
          const titleElement = Array.from(document.querySelectorAll('.question-title, .likert-question-header'))
            .find(el => {
              const text = el.textContent || '';
              const questionTitle = question.title || '';
              // 匹配题目标题的前30个字符（避免标题过长导致匹配失败）
              return text.includes(questionTitle.substring(0, 30));
            });
          
          if (titleElement) {
            titleElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
            const parentElement = titleElement.closest('.questionnaire-item, .question-item');
            if (parentElement) {
              parentElement.classList.add('error-highlight');
              setTimeout(() => {
                parentElement.classList.remove('error-highlight');
              }, 2000);
              return;
            }
          }
        }
        
        // 如果所有方案都失败，输出警告
        console.error(`[问卷调试] 无法找到题目 ${questionId} 的DOM元素，无法滚动`);
      });
    },
    
    // 检查滚动位置，判断是否到达底部
    checkScrollPosition() {
      // 使用 requestAnimationFrame 优化性能
      requestAnimationFrame(() => {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
        const windowHeight = window.innerHeight;
        const documentHeight = Math.max(
          document.body.scrollHeight,
          document.body.offsetHeight,
          document.documentElement.clientHeight,
          document.documentElement.scrollHeight,
          document.documentElement.offsetHeight
        );
        
        // 判断是否到达底部（允许10px的误差）
        const isAtBottom = scrollTop + windowHeight >= documentHeight - 10;
        // 如果页面内容高度小于等于窗口高度，认为已经在底部
        // 但只有当内容确实很短（小于窗口高度的1.2倍）时才认为在底部
        const isContentShort = documentHeight > 0 && documentHeight <= windowHeight * 1.2;
        const newIsScrolledToBottom = isAtBottom || isContentShort;
        
        // 调试日志
        console.log('[问卷调试] 滚动位置检查:', {
          scrollTop,
          windowHeight,
          documentHeight,
          isAtBottom,
          isContentShort,
          isScrolledToBottom: newIsScrolledToBottom,
          buttonClass: newIsScrolledToBottom ? 'relative-position' : 'fixed-bottom',
          currentIsScrolledToBottom: this.isScrolledToBottom,
          willChange: this.isScrolledToBottom !== newIsScrolledToBottom
        });
        
        // 只有状态改变时才更新，避免不必要的响应式更新
        if (this.isScrolledToBottom !== newIsScrolledToBottom) {
          this.isScrolledToBottom = newIsScrolledToBottom;
        }
      });
    },
    
    // 清除已填写题目的错误状态
    clearErrorsForAnsweredQuestions(newAnswers) {
      this.questionErrors.forEach(questionId => {
        const question = this.viewQuestions.find(q => q.id === questionId);
        if (!question || !question.required) {
          return;
        }
        
        // 检查该题目是否已填写
        let isAnswered = false;
        
        if (question.type === 'LIKERT_COMPOSITE') {
          const compositeAnswers = newAnswers[questionId];
          if (compositeAnswers && typeof compositeAnswers === 'object') {
            isAnswered = question.compositeConfig.likert.subQuestions.every(subQ => {
              const subAnswer = compositeAnswers[subQ.id];
              return subAnswer !== null && subAnswer !== undefined;
            });
          }
        } else if (question.type === 'MATRIX') {
          const matrixAnswer = newAnswers[questionId];
          if (matrixAnswer && typeof matrixAnswer === 'object' && Object.keys(matrixAnswer).length > 0) {
            isAnswered = question.matrixConfig?.rows?.every(row => {
              return matrixAnswer[row.id] !== null && matrixAnswer[row.id] !== undefined;
            }) || false;
          }
        } else if (question.painRatingRelated?.isYesNoQuestion) {
          // 疼痛评分题的是否题
          const answer = newAnswers[questionId];
          isAnswered = answer !== null && answer !== undefined;
        } else if (question.type === 'RATING' && question.painRatingRelated?.isRatingQuestion) {
          // 疼痛评分题的评分题：如果是否题选"否"，可以不填，视为已填写
          const yesNoQuestionId = question.painRatingRelated.yesNoQuestionId;
          const yesNoAnswer = newAnswers[yesNoQuestionId];
          if (yesNoAnswer === 'no') {
            // 如果选"否"，评分题可以不填，视为已填写
            isAnswered = true;
          } else if (yesNoAnswer === 'yes') {
            // 如果选"是"，检查评分题是否已填写
            const ratingAnswer = newAnswers[questionId];
            isAnswered = ratingAnswer !== null && ratingAnswer !== undefined;
          }
          // 如果是否题未选择，评分题也不能填写，视为未填写（isAnswered 保持 false）
        } else if (question.type === 'SINGLE_CHOICE') {
          // 单选题：检查"其他"选项的自定义输入
          const answer = newAnswers[questionId];
          if (answer !== null && answer !== undefined) {
            const answerStr = String(answer);
            if (answerStr.trim() !== '') {
              // 检查是否有"其他"选项（allowCustomInput=true）
              const otherOption = question.options?.find(opt => opt.allowCustomInput === true);
              if (otherOption) {
                // 如果选择了"其他"选项
                const selectedValue = answerStr.includes('|') ? answerStr.split('|')[0] : answerStr;
                if (selectedValue === otherOption.value) {
                  // 必须填写自定义内容（答案格式必须是"选项值|自定义文本"）
                  if (answerStr.includes('|')) {
                    const customText = answerStr.split('|')[1];
                    if (customText && customText.trim() !== '') {
                      isAnswered = true;
                    }
                  }
                  // 如果未填写自定义内容，isAnswered 保持为 false
                } else {
                  // 选择了非"其他"选项，视为已填写
                  isAnswered = true;
                }
              } else {
                // 没有"其他"选项，按普通单选题处理
                isAnswered = true;
              }
            }
          }
        } else {
          // 其他题型
          const answer = newAnswers[questionId];
          if (answer !== null && answer !== undefined) {
            if (typeof answer === 'string' && answer.trim() !== '') {
              isAnswered = true;
            } else if (Array.isArray(answer) && answer.length > 0) {
              isAnswered = true;
            } else if (typeof answer === 'number' || typeof answer === 'object') {
              isAnswered = true;
            }
          }
        }
        
        // 如果已填写，清除错误状态
        if (isAnswered) {
          this.questionErrors.delete(questionId);
        }
      });
    },
    
    async submitQuestionnaire() {
      this.submitting = true;
      try {
        // 转换答案格式：Likert复合题的答案需要展开为子问题答案
        const backendAnswers = this.transformAnswersForBackend(this.answers);
        
        const res = await post(`/api/my/exam/${this.examId}/questionnaire/submit`, {
          answers: backendAnswers
        });
        
        if (res.success) {
          // 提交成功后清除缓存
          this.clearAnswersCache();
          this.$message.success('问卷提交成功');
          this.$emit('submit-success');
        } else {
          this.$message.error(res.message || '问卷提交失败');
        }
      } catch (error) {
        console.error('提交问卷失败:', error);
        const errorMessage = error.message || '问卷提交失败，请稍后重试';
        
        // 尝试从错误消息中提取题目序号和标题，并滚动到该题目
        // 错误消息格式：第X题「xxx」为必填项，请填写
        const errorMatch = errorMessage.match(/第(\d+)题「([^」]+)」/);
        if (errorMatch) {
          const questionNumber = errorMatch[1];
          const questionTitle = errorMatch[2];
          // 显示友好的错误提示
          this.$message.error(`第${questionNumber}题，${questionTitle}没有填写`);
          
          // 查找匹配的题目
          const question = this.viewQuestions.find(q => q.title && q.title.includes(questionTitle));
          if (question) {
            // 标记为错误状态
            this.questionErrors.add(question.id);
            // 滚动到该题目
            this.scrollToQuestion(question.id);
          }
        } else {
          // 降级方案：尝试旧格式（兼容性处理）
          const titleMatch = errorMessage.match(/题目「([^」]+)」/);
          if (titleMatch) {
            const questionTitle = titleMatch[1];
            // 查找匹配的题目
            const question = this.viewQuestions.find(q => q.title && q.title.includes(questionTitle));
            if (question) {
              // 从题目ID提取序号
              const match = question.id.match(/q(\d+)/);
              const questionNumber = match && match[1] ? match[1] : '?';
              this.$message.error(`第${questionNumber}题，${questionTitle}没有填写`);
              // 标记为错误状态
              this.questionErrors.add(question.id);
              // 滚动到该题目
              this.scrollToQuestion(question.id);
            }
          } else {
            // 无法解析错误消息，显示原始错误
            this.$message.error(errorMessage);
          }
        }
      } finally {
        this.submitting = false;
      }
    },
    
    goBack() {
      // 如果用户跳过问卷，也清除缓存（避免下次进入时显示旧数据）
      this.clearAnswersCache();
      this.$emit('go-back');
    },
    
    /**
     * 从 localStorage 恢复已保存的答案（断点续填）
     */
    restoreAnswersFromCache() {
      if (!this.cacheKey) {
        return;
      }
      
      try {
        const cachedAnswers = getStorage(this.cacheKey, 'local');
        if (cachedAnswers && typeof cachedAnswers === 'object') {
          // 合并缓存的答案到当前 answers（只恢复已初始化的题目）
          Object.keys(cachedAnswers).forEach(questionId => {
            if (this.answers.hasOwnProperty(questionId)) {
              // 对于 Likert 复合题，需要深度合并
              if (this.viewQuestions.some(q => q.id === questionId && q.type === 'LIKERT_COMPOSITE')) {
                // Likert 复合题的答案是一个对象，需要深度合并
                if (typeof cachedAnswers[questionId] === 'object' && cachedAnswers[questionId] !== null) {
                  this.answers[questionId] = { ...this.answers[questionId], ...cachedAnswers[questionId] };
                }
              } else {
                // 其他题型直接赋值
                this.answers[questionId] = cachedAnswers[questionId];
              }
            }
          });
          
          console.log('[问卷调试] 从缓存恢复答案:', {
            cacheKey: this.cacheKey,
            restoredCount: Object.keys(cachedAnswers).length
          });
        }
      } catch (error) {
        console.warn('[问卷调试] 恢复缓存答案失败:', error);
      }
    },
    
    /**
     * 保存答案到 localStorage（防抖处理，避免频繁写入）
     */
    saveAnswersToCache(answers) {
      if (!this.cacheKey || !answers) {
        return;
      }
      
      // 清除之前的定时器
      if (this.saveCacheTimer) {
        clearTimeout(this.saveCacheTimer);
      }
      
      // 防抖：500ms 后保存
      this.saveCacheTimer = setTimeout(() => {
        try {
          setStorage({ key: this.cacheKey, value: answers }, 'local');
          console.log('[问卷调试] 答案已保存到缓存:', this.cacheKey);
        } catch (error) {
          console.warn('[问卷调试] 保存答案到缓存失败:', error);
        }
      }, 500);
    },
    
    /**
     * 清除答案缓存
     */
    clearAnswersCache() {
      if (!this.cacheKey) {
        return;
      }
      
      try {
        removeStorage([this.cacheKey], 'local');
        console.log('[问卷调试] 已清除答案缓存:', this.cacheKey);
      } catch (error) {
        console.warn('[问卷调试] 清除答案缓存失败:', error);
      }
    },
    
    /**
     * 处理答案更新（用于 Likert 复合题）
     */
    handleAnswersUpdate(newAnswers) {
      this.answers = newAnswers;
    },
    
    /**
     * 转换答案格式为后端期望的格式
     * Likert复合题的答案需要展开为子问题答案
     */
    transformAnswersForBackend(frontendAnswers) {
      const backendAnswers = { ...frontendAnswers };
      
      // 处理 Likert 复合题答案
      this.viewQuestions
        .filter(q => q.type === 'LIKERT_COMPOSITE')
        .forEach(composite => {
          const compositeAnswer = frontendAnswers[composite.id];
          if (compositeAnswer && typeof compositeAnswer === 'object') {
            // 将复合题答案展开为子问题答案
            Object.keys(compositeAnswer).forEach(subQuestionId => {
              backendAnswers[subQuestionId] = compositeAnswer[subQuestionId];
            });
            // 删除复合题答案（避免后端收到不认识的ID）
            delete backendAnswers[composite.id];
          }
        });
      
      // 疼痛评分题现在分为两个独立题目，不需要特殊处理答案转换
      
      return backendAnswers;
    }
  }
};
</script>

<style lang="less" scoped>
.questionnaire-page {
  position: relative;
  min-height: 100vh;
  background-color: #f5f5f5;
  
  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 400px;
    
    .loading-text {
      font-size: 16px;
      color: #999;
    }
  }
  
  .box-card {
    position: relative;
    color: #333;
    width: 60%;
    margin: 10% 20%;
    margin-bottom: 100px;  // 增加底部间距，确保按钮有足够空间
    background-color: #fff;
    border-radius: 4px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
    
    .el-card__header {
      font-size: 16px;
      font-weight: bold;
    }
    
    .card {
      font-size: 15px;
      padding: 30px;
      line-height: 1.6;
      
      .questionnaire-header {
        margin-bottom: 30px;
        padding-bottom: 20px;
        border-bottom: 1px solid #f0f0f0;
        
        h2 {
          margin: 0 0 20px 0;
          font-size: 24px;
          font-weight: 600;
          color: #0095ff;
          text-align: center;
          line-height: 1.5;
        }
        
        .questionnaire-warning {
          display: flex;
          align-items: center;
          background: linear-gradient(135deg, #fff3cd 0%, #ffe69c 100%);
          border: 2px solid #ffc107;
          border-radius: 8px;
          padding: 16px 20px;
          margin: 20px 0;
          box-shadow: 0 2px 8px rgba(255, 193, 7, 0.3);
          
          .warning-icon {
            font-size: 28px;
            color: #ff9800;
            margin-right: 12px;
            flex-shrink: 0;
            font-weight: bold;
            line-height: 1;
          }
          
          .warning-text {
            font-size: 16px;
            font-weight: 600;
            color: #e65100;
            line-height: 1.6;
            flex: 1;
          }
        }
        
        .questionnaire-intro {
          text-align: left;
          line-height: 2.2;
          color: #333;
          font-size: 15px;
          margin-top: 16px;
          
          p {
            margin: 0 0 12px 0;
            
            &:last-child {
              margin-bottom: 0;
            }
          }
        }
      }
      
      .submit-btn {
        padding: 0 30px;
        height: 40px;
        line-height: 40px;
        cursor: pointer;
        text-align: center;
        font-size: 16px;
        border-radius: 4px;
        transition: opacity 0.3s;
        
        // 相对定位（在内容流中的原始位置）
        &.relative-position {
          position: relative;
          float: right;
          margin: 30px 0 20px 0;
        }
      }
    }
  }
  
  // 固定定位的提交按钮（悬浮在底部，在 .card 外部）
  .submit-btn.fixed-bottom {
    position: fixed !important;
    bottom: 20px !important;
    right: calc(20% - 15px) !important;
    z-index: 1000 !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    margin: 0 !important;
    float: none !important;
    padding: 0 30px;
    height: 40px;
    line-height: 40px;
    cursor: pointer;
    text-align: center;
    font-size: 16px;
    border-radius: 4px;
    transition: opacity 0.3s;
        
    &:hover:not(.disabled) {
      opacity: 0.9;
    }
    
    &.has-errors {
      opacity: 0.7;
      // 按钮始终可点击，但视觉上提示有错误
    }
    
    &.submitting {
      opacity: 0.7;
      cursor: wait;
    }
  }
  
  // 错误题目高亮动画（在题目项上，支持普通题目和 Likert 复合题）
  :deep(.questionnaire-item.error-highlight),
  :deep(.question-item.error-highlight) {
    animation: errorPulse 0.5s ease-in-out 3;
  }
}

@keyframes errorPulse {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(255, 77, 79, 0.4);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(255, 77, 79, 0);
  }
}
</style>
